import React, { useState, useEffect, useRef } from 'react';
import { Trophy, ChevronLeft, Zap, Crown, Gift, Check, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from '../utils/feedback';
import { 
  subscribeToActiveBoss, 
  subscribeToTopHunters, 
  subscribeToUserParticipation, 
  queueTapDamage,
  claimBossReward, 
  getBossCycleInfo,
  canAttackBoss,
  DEFAULT_BOSS,
  renderBossTitle
} from '../services/bossService';

const BOSS_EMBERS = [
  { left: '8%', size: '3px', duration: 6.2, delay: -2.1, driftX: '18px', maxOp: 0.75, color: '#fbbf24', glow: 6 },
  { left: '16%', size: '2px', duration: 7.5, delay: -4.8, driftX: '-14px', maxOp: 0.6, color: '#f97316', glow: 4 },
  { left: '23%', size: '4px', duration: 5.8, delay: -1.2, driftX: '12px', maxOp: 0.85, color: '#f59e0b', glow: 8, isDiamond: true },
  { left: '31%', size: '2.5px', duration: 8.1, delay: -5.5, driftX: '-20px', maxOp: 0.65, color: '#c084fc', glow: 5 },
  { left: '38%', size: '3.5px', duration: 6.7, delay: -3.0, driftX: '15px', maxOp: 0.8, color: '#fef08a', glow: 7 },
  { left: '46%', size: '2px', duration: 7.2, delay: -0.8, driftX: '-10px', maxOp: 0.55, color: '#6eb583', glow: 4 },
  { left: '52%', size: '4.5px', duration: 5.5, delay: -3.8, driftX: '22px', maxOp: 0.9, color: '#fbbf24', glow: 9, isDiamond: true },
  { left: '59%', size: '2.5px', duration: 7.8, delay: -2.5, driftX: '-16px', maxOp: 0.7, color: '#f97316', glow: 5 },
  { left: '67%', size: '3px', duration: 6.5, delay: -5.0, driftX: '14px', maxOp: 0.75, color: '#f59e0b', glow: 6 },
  { left: '74%', size: '2px', duration: 8.4, delay: -1.7, driftX: '-12px', maxOp: 0.6, color: '#c084fc', glow: 4 },
  { left: '82%', size: '4px', duration: 6.0, delay: -4.2, driftX: '16px', maxOp: 0.85, color: '#fef08a', glow: 8, isDiamond: true },
  { left: '89%', size: '2.5px', duration: 7.1, delay: -2.9, driftX: '-18px', maxOp: 0.65, color: '#f97316', glow: 5 },
  { left: '95%', size: '3px', duration: 6.8, delay: -0.5, driftX: '10px', maxOp: 0.7, color: '#fbbf24', glow: 6 },
  { left: '12%', size: '3.5px', duration: 5.9, delay: -3.6, driftX: '-15px', maxOp: 0.75, color: '#f59e0b', glow: 7 },
  { left: '27%', size: '2px', duration: 7.9, delay: -6.1, driftX: '14px', maxOp: 0.6, color: '#fef08a', glow: 4 },
  { left: '42%', size: '4px', duration: 6.4, delay: -1.9, driftX: '-22px', maxOp: 0.8, color: '#fbbf24', glow: 8, isDiamond: true },
  { left: '63%', size: '2px', duration: 8.2, delay: -4.4, driftX: '18px', maxOp: 0.55, color: '#6eb583', glow: 4 },
  { left: '78%', size: '3.5px', duration: 5.7, delay: -2.3, driftX: '-12px', maxOp: 0.8, color: '#f97316', glow: 7 },
  { left: '91%', size: '2px', duration: 7.4, delay: -5.8, driftX: '15px', maxOp: 0.65, color: '#c084fc', glow: 5 }
];

export default function WorldBossView({ currentProfile, embedded = false }) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const userName = currentProfile?.name || currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Athlète';

  // --- ÉTATS TEMPS RÉEL FIRESTORE ---
  const [boss, setBoss] = useState(DEFAULT_BOSS);
  const [topHunters, setTopHunters] = useState([]);
  const [userPart, setUserPart] = useState(null);
  const [isClaiming, setIsClaiming] = useState(false);

  // --- ÉTATS LOCAUX OPTIMISTES (RÉACTIVITÉ INSTANTANÉE SANS AUCUNE LATENCE) ---
  const [localBonusDamage, setLocalBonusDamage] = useState(0);
  const [localHpOffset, setLocalHpOffset] = useState(0);

  // Animations & Effets Visuels
  const [isHit, setIsHit] = useState(false);
  const [screenShake, setScreenShake] = useState(false);
  const [floatingDamages, setFloatingDamages] = useState([]);
  const nextId = useRef(0);

  // 1. Écoute du Boss Actif
  useEffect(() => {
    const unsub = subscribeToActiveBoss((activeBoss) => {
      if (activeBoss) {
        setBoss(activeBoss);
      }
    });
    return () => unsub();
  }, []);

  // 2. Écoute du Top Chasseurs Réels de la Communauté
  useEffect(() => {
    if (!boss?.id) return;
    const unsub = subscribeToTopHunters(boss.id, (hunters) => {
      setTopHunters(hunters);
    }, 3);
    return () => unsub();
  }, [boss?.id]);

  // 3. Écoute de la Participation et du Rang Réel de l'Utilisateur
  useEffect(() => {
    if (!boss?.id || !currentProfile?.id) return;
    const unsub = subscribeToUserParticipation(boss.id, currentProfile.id, (part) => {
      setUserPart(part);
    });
    return () => unsub();
  }, [boss?.id, currentProfile?.id]);

  // Réconciliation fluide de l'optimistic UI quand Firestore confirme l'écriture
  const prevServerDamage = useRef(null);
  const prevServerHp = useRef(null);

  useEffect(() => {
    if (userPart?.damage !== undefined) {
      if (prevServerDamage.current !== null) {
        const delta = (userPart.damage || 0) - prevServerDamage.current;
        if (delta > 0) {
          setLocalBonusDamage(prev => Math.max(0, prev - delta));
        }
      }
      prevServerDamage.current = userPart.damage || 0;
    }
  }, [userPart?.damage]);

  useEffect(() => {
    if (boss?.currentHp !== undefined) {
      if (prevServerHp.current !== null) {
        const delta = prevServerHp.current - boss.currentHp;
        if (delta > 0) {
          setLocalHpOffset(prev => Math.max(0, prev - delta));
        }
      }
      prevServerHp.current = boss.currentHp;
    }
  }, [boss?.currentHp]);

  const maxHp = boss.maxHp || 1000000;
  const baseHp = boss.currentHp !== undefined ? boss.currentHp : 1000000;
  const currentHp = Math.max(0, baseHp - localHpOffset);
  const hpPercent = Math.max(0, Math.min(100, (currentHp / maxHp) * 100));
  const bossCycle = boss?.cycleInfo || getBossCycleInfo();
  const isDead = currentHp <= 0 || boss.status === 'defeated';
  const isRaidExpired = boss.status === 'expired' || (!isDead && bossCycle.isRestPeriod);
  const isCombatOver = isDead || isRaidExpired;
  const isLowHp = currentHp > 0 && hpPercent <= 10 && !isCombatOver;
  const rewardCoins = boss.rewardCoins || 150;
  const damageUnit = boss.damageUnit || (boss.ruleType === 'reps' ? 'reps' : 'kg');
  const myDamage = (userPart?.damage || 0) + localBonusDamage;
  const userRank = userPart?.rank || (myDamage > 0 ? 1 : null);
  const hasEligibleWorkouts = (userPart?.workoutsCount || 0) >= 1;

  // Nettoyage fluide des dégâts flottants
  useEffect(() => {
    if (floatingDamages.length === 0) return;
    const timer = setTimeout(() => {
      setFloatingDamages(prev => prev.slice(1));
    }, 900);
    return () => clearTimeout(timer);
  }, [floatingDamages]);

  // Déclenchement local des animations
  const triggerVisualDamage = (amount, isCrit = false, clickX = null, clickY = null, isTap = false) => {
    setIsHit(true);
    setTimeout(() => setIsHit(false), isTap ? 120 : 200);

    if (amount >= 5000) {
      setScreenShake(true);
      setTimeout(() => setScreenShake(false), 350);
    }

    const id = nextId.current++;
    const x = clickX !== null ? clickX : Math.random() * 80 - 40;
    const y = clickY !== null ? clickY : Math.random() * 40 - 20;

    setFloatingDamages(prev => [
      ...prev,
      {
        id,
        amount,
        isCrit,
        isTap,
        x,
        y,
        text: isTap ? `-1 kg 👊` : `-${amount.toLocaleString()} kg${isCrit ? ' 💥' : ''}`
      }
    ]);
  };

  // Tapotement interactif avec le doigt ou la souris (0ms de latence)
  const handleDragonTap = (e) => {
    if (isCombatOver) {
      if (isRaidExpired && !isDead) {
        toast.info("L'arène est en pause. Le prochain boss arrivera lundi prochain !");
      }
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left - rect.width / 2;
    const clickY = e.clientY - rect.top - rect.height / 2;

    // 1. Déclenchement visuel immédiat
    triggerVisualDamage(1, false, clickX, clickY, true);

    // 2. Mise à jour optimiste instantanée des PV et des kg
    setLocalBonusDamage(prev => prev + 1);
    setLocalHpOffset(prev => prev + 1);

    // 3. Synchronisation par lot en arrière-plan vers Firestore
    if (currentProfile?.id) {
      queueTapDamage(currentProfile, (res) => {
        if (res?.isDefeated) {
          toast.success(`🎉 DRAGON TERRASSÉ ! Le coup final a achevé ${boss.name} !`, { icon: '👑' });
        }
      });
    }
  };

  // Récupération de la récompense de 150 pièces
  const handleClaimReward = async () => {
    if (!currentProfile?.id) {
      toast.error("Sélectionne un profil pour récupérer ton butin.");
      return;
    }
    setIsClaiming(true);
    try {
      const res = await claimBossReward(currentProfile);
      toast.success(`🎉 Félicitations ! +${res.rewardCoins} Pièces ajoutées à ton compte !`, { icon: '🪙' });
    } catch (err) {
      toast.error(err.message || "Erreur lors de la récupération du butin.");
    } finally {
      setIsClaiming(false);
    }
  };

  // --- COMPOSANTS DE CARTES PARTAGÉS (VUE COMBAT & VUE REPOS) ---
  const renderContributionCard = () => (
    <div style={{
      backgroundColor: 'var(--bg-card, #1c1a24)',
      border: '1px solid var(--border-color, #2f2c3d)',
      borderRadius: '16px',
      padding: '1.2rem',
      boxShadow: 'var(--shadow)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      gap: '0.75rem',
      boxSizing: 'border-box'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.88rem', fontWeight: 800, color: 'var(--text-secondary, #9a94ab)' }}>
          <Zap size={16} color="var(--primary-color, #8c7fbf)" /> Ta Contribution au Raid
        </div>
        <span style={{
          fontSize: '0.72rem',
          fontWeight: 800,
          color: hasEligibleWorkouts ? 'var(--secondary-color, #6eb583)' : (myDamage > 0 ? '#fbbf24' : 'var(--text-secondary, #9a94ab)'),
          backgroundColor: hasEligibleWorkouts ? 'rgba(110, 181, 131, 0.12)' : (myDamage > 0 ? 'rgba(251, 191, 36, 0.12)' : 'rgba(255, 255, 255, 0.05)'),
          padding: '0.15rem 0.5rem',
          borderRadius: '5px'
        }}>
          {hasEligibleWorkouts ? (userRank ? `Rang #${userRank}` : 'Éligible') : (myDamage > 0 ? '1 séance requise' : 'En attente')}
        </span>
      </div>

      <div>
        <div style={{ fontSize: '1.75rem', fontWeight: 900, color: 'var(--primary-color-hover, #a89fcf)', lineHeight: 1 }}>
          {myDamage.toLocaleString()} {damageUnit}
        </div>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary, #9a94ab)', marginTop: '0.25rem' }}>
          {userPart?.workoutsCount || 0} séance(s) comptabilisée(s) {bossCycle.isRestPeriod ? 'lors du dernier raid' : 'cette semaine'}
        </div>
      </div>

      {/* BUTIN COLLECTIF : 150 PIÈCES GARANTIES (EXIGE AU MOINS 1 VRAIE SÉANCE) */}
      {isDead ? (
        userPart?.rewardClaimed ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.4rem',
            backgroundColor: 'rgba(110, 181, 131, 0.18)',
            border: '1px solid var(--secondary-color, #6eb583)',
            padding: '0.6rem 0.8rem',
            borderRadius: '10px',
            fontSize: '0.78rem',
            color: 'var(--secondary-color, #6eb583)',
            fontWeight: 800
          }}>
            <Check size={16} /> Butin de {rewardCoins} 🪙 récupéré !
          </div>
        ) : hasEligibleWorkouts ? (
          <button
            onClick={handleClaimReward}
            disabled={isClaiming}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              background: 'linear-gradient(135deg, #df9b3e 0%, #bd7b24 100%)',
              color: '#ffffff',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.4)',
              border: 'none',
              padding: '0.75rem 1rem',
              borderRadius: '12px',
              fontSize: '0.85rem',
              fontWeight: 800,
              cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(223, 155, 62, 0.35)',
              transition: 'transform 0.15s ease'
            }}
          >
            <Gift size={18} />
            <span>{isClaiming ? 'Attribution...' : `Réclamer mon butin (+${rewardCoins} 🪙) !`}</span>
          </button>
        ) : (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
            border: '1px solid var(--border-color, #2f2c3d)',
            padding: '0.55rem 0.75rem',
            borderRadius: '10px',
            fontSize: '0.75rem',
            color: 'var(--text-secondary, #9a94ab)'
          }}>
            <span>Boss vaincu ! Au moins 1 séance en salle était requise pour obtenir les {rewardCoins} pièces.</span>
          </div>
        )
      ) : isRaidExpired ? (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.45rem',
          backgroundColor: 'rgba(255, 255, 255, 0.04)',
          border: '1px solid var(--border-color, #2f2c3d)',
          padding: '0.55rem 0.75rem',
          borderRadius: '10px',
          fontSize: '0.76rem',
          color: 'var(--text-secondary, #9a94ab)'
        }}>
          <span>💀 Le boss n'a pas été vaincu à temps et s'est enfui. Rendez-vous au prochain raid !</span>
        </div>
      ) : (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.45rem',
          backgroundColor: hasEligibleWorkouts ? 'rgba(110, 181, 131, 0.1)' : 'rgba(251, 191, 36, 0.08)',
          border: hasEligibleWorkouts ? '1px solid rgba(110, 181, 131, 0.25)' : '1px solid rgba(251, 191, 36, 0.2)',
          padding: '0.55rem 0.75rem',
          borderRadius: '10px',
          fontSize: '0.76rem',
          color: 'var(--text-primary, #f0eef5)'
        }}>
          <Gift size={16} color={hasEligibleWorkouts ? "var(--secondary-color, #6eb583)" : "#fbbf24"} />
          <span>
            {hasEligibleWorkouts 
              ? <>✓ Tu es <strong>éligible</strong> aux <strong>+{rewardCoins} 🪙</strong> de butin collectif !</>
              : <>Butin collectif : <strong>+{rewardCoins} 🪙</strong> (1 séance requise pour débloquer)</>}
          </span>
        </div>
      )}
    </div>
  );

  const renderTopHuntersCard = () => (
    <div style={{
      backgroundColor: 'var(--bg-card, #1c1a24)',
      border: '1px solid var(--border-color, #2f2c3d)',
      borderRadius: '16px',
      padding: '1.2rem',
      boxShadow: 'var(--shadow)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      boxSizing: 'border-box'
    }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.85rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Trophy size={18} color="#fbbf24" />
            <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 800 }}>Top meilleurs chasseurs</h3>
          </div>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary, #9a94ab)' }}>
            {bossCycle.isRestPeriod ? 'Bilan du raid' : 'Tonnage hebdo'}
          </span>
        </div>

        {/* LISTE TOP 3 DES VRAIS ATHLÈTES */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
          {topHunters.length === 0 ? (
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary, #9a94ab)', fontStyle: 'italic', padding: '0.5rem 0' }}>
              Aucun athlète n'a encore enregistré de séance cette semaine.
            </div>
          ) : (
            topHunters.map(hunter => (
              <div 
                key={hunter.rank || hunter.id}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between', 
                  padding: '0.5rem 0.7rem', 
                  backgroundColor: 'rgba(255,255,255,0.03)', 
                  borderRadius: '10px',
                  border: '1px solid rgba(255,255,255,0.05)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem' }}>
                  <span style={{ fontSize: '1rem' }}>{hunter.medal}</span>
                  <div>
                    <div style={{ fontSize: '0.82rem', fontWeight: 800, color: 'var(--text-primary, #f0eef5)' }}>{hunter.name}</div>
                    <div style={{ fontSize: '0.68rem', color: hunter.isKing ? '#fbbf24' : 'var(--primary-color-hover, #a89fcf)', fontWeight: 600 }}>
                      {hunter.title}
                    </div>
                  </div>
                </div>
                <span style={{ fontSize: '0.82rem', fontWeight: 800, color: 'var(--text-secondary, #9a94ab)' }}>
                  {(hunter.damage || 0).toLocaleString()} {damageUnit}
                </span>
              </div>
            ))
          )}

          {/* Emplacement libre si moins de 3 participants */}
          {topHunters.length > 0 && topHunters.length < 3 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0.45rem 0.7rem',
              backgroundColor: 'rgba(255,255,255,0.015)',
              borderRadius: '10px',
              border: '1px dashed rgba(255,255,255,0.08)',
              fontSize: '0.75rem',
              color: 'var(--text-secondary, #9a94ab)'
            }}>
              <span>{topHunters.length === 1 ? '🥈' : '🥉'} Place libre sur le podium</span>
              <span style={{ fontSize: '0.7rem', fontStyle: 'italic' }}>
                {bossCycle.isRestPeriod ? 'Place vacante' : 'Enregistre une séance !'}
              </span>
            </div>
          )}

          {/* BANNIÈRE DE L'UTILISATEUR CONNECTÉ */}
          <div style={{
            marginTop: '0.45rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0.65rem 0.75rem',
            backgroundColor: 'rgba(140, 127, 191, 0.14)',
            border: '1.5px solid var(--primary-color, #8c7fbf)',
            borderRadius: '12px',
            boxShadow: '0 0 16px rgba(140, 127, 191, 0.2)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <div style={{
                fontSize: '0.72rem',
                fontWeight: 900,
                backgroundColor: 'var(--primary-color, #8c7fbf)',
                color: '#fff',
                padding: '0.18rem 0.45rem',
                borderRadius: '6px'
              }}>
                #{userRank || '-'}
              </div>
              <div>
                <div style={{ fontSize: '0.82rem', fontWeight: 800, color: 'var(--text-primary, #f0eef5)' }}>
                  Toi ({userName})
                </div>
                <div style={{
                  fontSize: '0.68rem',
                  color: hasEligibleWorkouts ? 'var(--secondary-color, #6eb583)' : (myDamage > 0 ? '#fbbf24' : 'var(--text-secondary, #9a94ab)'),
                  fontWeight: 600
                }}>
                  {hasEligibleWorkouts 
                    ? (isDead ? (userPart?.rewardClaimed ? "✓ Butin récupéré" : `🎁 Butin prêt (+${rewardCoins} 🪙)`) : `✓ Éligible au Butin (+${rewardCoins} 🪙)`)
                    : (myDamage > 0 ? "🏋️ 1 séance requise pour le butin (+150 🪙)" : "Enregistre une séance pour participer")}
                </div>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <span style={{ fontSize: '0.88rem', fontWeight: 900, color: 'var(--primary-color-hover, #a89fcf)' }}>
                {myDamage.toLocaleString()} {damageUnit}
              </span>
              <div style={{ fontSize: '0.65rem', color: 'var(--text-secondary, #9a94ab)' }}>
                {userRank ? `Rang #${userRank}` : 'Non classé'}
              </div>
            </div>
          </div>

        </div>
      </div>
      
      <div style={{ marginTop: '0.8rem', fontSize: '0.7rem', color: 'var(--text-secondary, #9a94ab)', fontStyle: 'italic', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '0.5rem' }}>
        {isDead ? 'Titres de prestige attribués sur les profils des champions !' : 'Le Top 3 et le coup final remportent les titres de prestige à la défaite du boss.'}
      </div>
    </div>
  );

  const renderPrestigeTitlesCard = () => (
    <div style={{
      backgroundColor: 'var(--bg-card, #1c1a24)',
      border: '1px solid var(--border-color, #2f2c3d)',
      borderRadius: '16px',
      padding: '1.2rem',
      boxShadow: 'var(--shadow)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      gap: '0.5rem',
      boxSizing: 'border-box'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.88rem', fontWeight: 800, color: 'var(--text-secondary, #9a94ab)' }}>
        <Crown size={16} color="#fbbf24" /> Titres en jeu (7 jours)
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.76rem' }}>
          <span>👑 <strong>Top 1</strong></span>
          <span style={{ color: '#fbbf24', fontWeight: 700 }}>Fléau des Titans</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.76rem' }}>
          <span>⚔️ <strong>Top 2-3</strong></span>
          <span style={{ color: 'var(--primary-color-hover, #a89fcf)', fontWeight: 700 }}>Champion du Raid</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.76rem' }}>
          <span>🎯 <strong>Dernier Coup</strong></span>
          <span style={{ color: 'var(--secondary-color, #6eb583)', fontWeight: 700 }}>Coup de Grâce</span>
        </div>
      </div>

      <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary, #9a94ab)', fontStyle: 'italic', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '0.4rem' }}>
        Attribués directement sur le profil dès la mort du boss (actifs jusqu'au prochain boss).
      </div>
    </div>
  );

  return (
    <div className={`boss-arena-wrapper ${screenShake ? 'shake-screen' : ''}`} style={{
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
      color: 'var(--text-primary, #f0eef5)',
      paddingBottom: '2.5rem',
      position: 'relative'
    }}>
      <style>{`
        @keyframes floatIdle {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-10px) rotate(0.6deg); }
        }
        @keyframes wingBeat {
          0%, 100% { transform: scaleX(1) scaleY(1); }
          50% { transform: scaleX(1.05) scaleY(0.96); }
        }
        @keyframes eyeGlow {
          0%, 100% { filter: drop-shadow(0 0 6px var(--primary-color, #8c7fbf)) drop-shadow(0 0 14px var(--secondary-color, #6eb583)); }
          50% { filter: drop-shadow(0 0 12px #a855f7) drop-shadow(0 0 20px #10b981); }
        }
        @keyframes floatEmber {
          0% {
            transform: translateY(0) translateX(0) scale(0.8);
            opacity: 0;
          }
          15% {
            opacity: var(--max-op, 0.8);
          }
          50% {
            transform: translateY(-200px) translateX(calc(var(--drift-x, 15px) * 0.5)) scale(1.1);
            opacity: var(--max-op, 0.8);
          }
          85% {
            opacity: calc(var(--max-op, 0.8) * 0.7);
          }
          100% {
            transform: translateY(-460px) translateX(var(--drift-x, 15px)) scale(0.3);
            opacity: 0;
          }
        }
        @keyframes damagePop {
          0% { opacity: 0; transform: translate(var(--dx), var(--dy)) scale(0.6); }
          20% { opacity: 1; transform: translate(var(--dx), calc(var(--dy) - 20px)) scale(1.15); }
          100% { opacity: 0; transform: translate(var(--dx), calc(var(--dy) - 55px)) scale(0.9); }
        }
        .shake-screen {
          animation: shake 0.35s cubic-bezier(.36,.07,.19,.97) both;
        }
        @keyframes shake {
          10%, 90% { transform: translate3d(-1px, 0, 0); }
          20%, 80% { transform: translate3d(2px, 0, 0); }
          30%, 50%, 70% { transform: translate3d(-4px, 0, 0); }
          40%, 60% { transform: translate3d(4px, 0, 0); }
        }
        .dragon-container {
          cursor: pointer;
          user-select: none;
          -webkit-tap-highlight-color: transparent;
          transition: transform 0.08s ease;
        }
        .dragon-container:active {
          transform: scale(0.96) !important;
        }
        .dragon-container.hit svg {
          filter: brightness(2) contrast(1.5) drop-shadow(0 0 25px #ffffff) !important;
          transition: filter 0.05s ease;
        }

        .boss-title-strikethrough {
          text-decoration: line-through 2.5px #ef4444 !important;
          -webkit-text-decoration-line: line-through !important;
          -webkit-text-decoration-color: #ef4444 !important;
          -webkit-text-decoration-style: solid !important;
          text-decoration-color: #ef4444 !important;
          text-decoration-thickness: 2.5px !important;
          color: #94a3b8 !important;
          opacity: 0.85 !important;
          margin: 0 4px !important;
          font-weight: 700 !important;
          display: inline-block !important;
        }

        /* --- LAYOUT MOBILE PAR DÉFAUT (INCHANGÉ, PARFAIT SUR TÉLÉPHONE) --- */
        .boss-main-layout {
          display: flex;
          flex-direction: column;
          gap: 0.85rem;
          width: 100%;
        }
        .boss-arena-box {
          width: 100%;
          box-sizing: border-box;
        }
        .boss-arena-center {
          width: 100%;
          margin: 0.2rem auto 0.5rem auto;
        }
        .dashboard-cards-col {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          width: 100%;
        }

        /* --- LAYOUT PC / GRAND ÉCRAN (2 COLONNES HARMONIEUSES, MÊME HAUTEUR EXACTE) --- */
        @media (min-width: 901px) {
          .boss-main-layout {
            display: grid !important;
            grid-template-columns: 1.15fr 0.85fr !important;
            gap: 1.5rem !important;
            align-items: stretch !important;
          }
          .boss-arena-box {
            height: 100% !important;
            display: flex !important;
            flex-direction: column !important;
            justify-content: space-between !important;
            padding: 1.8rem 1.4rem !important;
            box-sizing: border-box !important;
          }
          .boss-arena-top {
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            width: 100% !important;
          }
          .boss-arena-center {
            flex: 1 !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            justify-content: center !important;
            min-height: 280px !important;
            margin: 1rem 0 !important;
            width: 100% !important;
          }
          .boss-arena-center .dragon-container {
            max-width: 400px !important;
          }
          .boss-arena-center .dragon-container svg {
            max-width: 380px !important;
          }
          .boss-arena-bottom {
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            width: 100% !important;
          }
          .dashboard-cards-col {
            display: flex !important;
            flex-direction: column !important;
            gap: 1rem !important;
            height: 100% !important;
            justify-content: space-between !important;
          }
        }
      `}</style>

      {/* Halo lumineux d'ambiance (couleur habituelle conservée) */}
      <div style={{
        position: 'absolute',
        top: '0%',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '500px',
        height: '500px',
        background: 'radial-gradient(circle, rgba(140, 127, 191, 0.14) 0%, transparent 70%)',
        pointerEvents: 'none',
        zIndex: 0
      }} />

      <div style={{ width: '100%', position: 'relative', zIndex: 1, boxSizing: 'border-box' }}>

        {/* NAVIGATION HAUTE (HORS-ONGLET SEULEMENT) */}
        {!embedded && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', marginBottom: '1rem', width: '100%' }}>
            <button 
              onClick={() => navigate('/social')}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.4rem',
                backgroundColor: 'var(--bg-card, #1c1a24)', border: '1px solid var(--border-color, #2f2c3d)',
                color: 'var(--text-secondary, #9a94ab)', padding: '0.45rem 0.85rem', borderRadius: '10px',
                cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600,
                boxShadow: 'var(--shadow)'
              }}
            >
              <ChevronLeft size={16} /> Retour au Social
            </button>
          </div>
        )}

        {bossCycle.isRestPeriod ? (
          /* ========================================================= */
          /* VUE REPOS : PAS DE BOSS AFFICHÉ                           */
          /* ========================================================= */
          <div style={{ maxWidth: '850px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '1.2rem', width: '100%' }}>
            <div style={{
              backgroundColor: 'var(--bg-card, #1c1a24)',
              border: '1.5px solid rgba(140, 127, 191, 0.3)',
              boxShadow: '0 4px 20px rgba(140, 127, 191, 0.15), var(--shadow)',
              borderRadius: '20px',
              padding: '2rem 1.4rem',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.6rem'
            }}>
              <div style={{
                width: '56px', height: '56px', borderRadius: '50%',
                backgroundColor: 'rgba(140, 127, 191, 0.14)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--primary-color, #8c7fbf)', marginBottom: '0.2rem'
              }}>
                <Clock size={28} />
              </div>
              <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 900, color: 'var(--text-primary, #f0eef5)', lineHeight: 1.4 }}>
                L'Arène est au repos. Prochain boss le{' '}
                <span style={{ color: 'var(--primary-color-hover, #a89fcf)' }}>
                  {bossCycle.nextBossStart ? `${new Date(bossCycle.nextBossStart).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} à 00h00` : 'lundi prochain à 00h00'}
                </span>
              </h2>
              <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary, #9a94ab)', maxWidth: '520px' }}>
                Le boss régénère ses forces. Retrouve ci-dessous ta contribution au raid ainsi que le classement des meilleurs chasseurs !
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.2rem', width: '100%' }}>
              {renderContributionCard()}
              {renderTopHuntersCard()}
            </div>
          </div>
        ) : (
          /* ========================================================= */
          /* LAYOUT PRINCIPAL : ARÈNE (GAUCHE) & STATS RAID (DROITE)   */
          /* ========================================================= */
          <div className="boss-main-layout">

            {/* COLONNE GAUCHE : ARÈNE DU BOSS */}
            <div className="boss-arena-box" style={{
              backgroundColor: 'var(--bg-card, #1c1a24)',
              border: isLowHp 
                ? '1.5px solid var(--danger-color, #ef4444)' 
                : '1px solid var(--border-color, #2f2c3d)',
              boxShadow: isLowHp 
                ? '0 0 20px rgba(239, 68, 68, 0.22), var(--shadow)' 
                : 'var(--shadow)',
              borderRadius: '20px',
              padding: '1.3rem 1rem',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
              boxSizing: 'border-box',
              transition: 'border 0.3s ease, box-shadow 0.3s ease'
            }}>

              {/* Lueur volcanique ambiante d'arrière-plan */}
              <div style={{
                position: 'absolute',
                inset: 0,
                background: isDead
                  ? 'none'
                  : 'radial-gradient(circle at 50% 60%, rgba(245, 158, 11, 0.08) 0%, rgba(168, 85, 247, 0.05) 50%, transparent 80%)',
                pointerEvents: 'none',
                zIndex: 0
              }} />

              {/* Pluie de braises et étincelles de forge ambiantes indépendantes */}
              {!isDead && (
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  overflow: 'hidden',
                  pointerEvents: 'none',
                  zIndex: 0
                }}>
                  {BOSS_EMBERS.map((ember, i) => (
                    <span
                      key={i}
                      style={{
                        position: 'absolute',
                        bottom: '-16px',
                        left: ember.left,
                        width: ember.size,
                        height: ember.size,
                        borderRadius: ember.isDiamond ? '2px' : '50%',
                        transform: ember.isDiamond ? 'rotate(45deg)' : 'none',
                        backgroundColor: ember.color,
                        boxShadow: `0 0 ${ember.glow}px ${ember.color}`,
                        opacity: 0,
                        animation: `floatEmber ${ember.duration}s ease-in-out infinite`,
                        animationDelay: `${ember.delay}s`,
                        '--drift-x': ember.driftX,
                        '--max-op': ember.maxOp
                      }}
                    />
                  ))}
                </div>
              )}

            <div className="boss-arena-top" style={{ width: '100%', position: 'relative', zIndex: 1 }}>
              {/* BADGES & STATUT DU BOSS */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginBottom: '0.4rem', flexWrap: 'wrap' }}>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.3rem',
                  padding: '0.2rem 0.55rem',
                  backgroundColor: isDead 
                    ? 'rgba(110, 181, 131, 0.18)' 
                    : isRaidExpired 
                      ? 'rgba(107, 114, 128, 0.25)' 
                      : 'rgba(140, 127, 191, 0.18)',
                  color: isDead 
                    ? 'var(--secondary-color, #6eb583)' 
                    : isRaidExpired 
                      ? '#d1d5db' 
                      : 'var(--primary-color-hover, #a89fcf)',
                  borderRadius: '20px',
                  fontSize: '0.68rem',
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: '0.4px'
                }}>
                  {isDead ? '🏆 Boss Vaincu' : isRaidExpired ? '💀 Raid Terminé' : '⚔️ Évènement Spécial'}
                </span>

                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.3rem',
                  padding: '0.2rem 0.55rem',
                  backgroundColor: 'rgba(110, 181, 131, 0.12)',
                  color: 'var(--secondary-color, #6eb583)',
                  borderRadius: '20px',
                  fontSize: '0.68rem',
                  fontWeight: 700
                }}>
                  ⚖️ {boss.ruleLabel || '1 kg = 1 dégât'}
                </span>
              </div>

              {/* NOM DU BOSS */}
              <h1 style={{
                margin: '0.2rem 0 0.15rem 0',
                fontSize: '1.9rem',
                fontWeight: 900,
                letterSpacing: '-0.5px',
                color: 'var(--text-primary, #f0eef5)'
              }}>
                {boss.name || 'Dracofonte'}
              </h1>
              <p style={{
                margin: '0 0 0.9rem 0',
                fontSize: '0.82rem',
                color: 'var(--text-secondary, #9a94ab)',
                fontStyle: 'italic'
              }}>
                {renderBossTitle(boss.title || 'Le Léviathan de Fonte Ancestral')}
              </p>

              {/* BARRE DE VIE RÉACTOR (LIVE SYNC & INSTANT FEEDBACK) */}
              <div style={{ maxWidth: '440px', margin: '0 auto 0.75rem auto', width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.35rem', fontSize: '0.78rem' }}>
                  <span style={{ color: isLowHp ? 'var(--danger-color, #ef4444)' : 'var(--text-secondary, #9a94ab)', fontWeight: 600, transition: 'color 0.3s ease' }}>Points de Vie</span>
                  <span style={{ fontWeight: 900, color: isDead ? '#6b7280' : (isLowHp ? 'var(--danger-color, #ef4444)' : 'var(--text-primary, #f0eef5)'), transition: 'color 0.3s ease' }}>
                    {currentHp.toLocaleString()} / {maxHp.toLocaleString()} PV ({hpPercent.toFixed(1)}%)
                  </span>
                </div>

                <div style={{
                  width: '100%',
                  height: '14px',
                  backgroundColor: 'rgba(0, 0, 0, 0.35)',
                  borderRadius: '8px',
                  border: '1px solid var(--border-color, #2f2c3d)',
                  overflow: 'hidden',
                  boxShadow: 'inset 0 2px 6px rgba(0,0,0,0.5)'
                }}>
                  <div style={{
                    width: `${hpPercent}%`,
                    height: '100%',
                    background: isDead
                      ? 'var(--text-secondary, #6b7280)'
                      : isLowHp
                        ? 'linear-gradient(90deg, #ec4899 0%, #f43f5e 100%)'
                        : 'linear-gradient(90deg, #6d28d9 0%, var(--primary-color, #8c7fbf) 50%, var(--secondary-color, #6eb583) 100%)',
                    borderRadius: '8px',
                    transition: 'width 0.25s cubic-bezier(0.2, 0.8, 0.2, 1), background 0.3s ease',
                    boxShadow: isLowHp ? '0 0 10px rgba(244, 63, 94, 0.6)' : undefined
                  }} />
                </div>
              </div>
            </div>

            {/* DRAGON 2D VECTORIEL FLUIDE & RESPONSIVE (TAP POUR ATTAQUER) */}
            <div className="boss-arena-center" style={{ position: 'relative', zIndex: 1 }}>
              <div 
                onClick={isDead ? undefined : handleDragonTap}
                className={`dragon-container ${isHit ? 'hit' : ''}`}
                style={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: '320px',
                  margin: '0.2rem auto 0.5rem auto',
                  animation: 'floatIdle 4.5s ease-in-out infinite',
                  cursor: isDead ? 'default' : 'pointer',
                  pointerEvents: isDead ? 'none' : 'auto'
                }}
                title={isDead ? "Le dragon a été vaincu !" : "Tapote avec ton doigt ou ta souris pour frapper !"}
              >
                <svg 
                  width="100%" 
                  height="auto" 
                  viewBox="0 0 320 280" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  style={{
                    maxWidth: '300px',
                    overflow: 'visible',
                    filter: isDead 
                      ? 'grayscale(1) opacity(0.35)' 
                      : 'drop-shadow(0 0 20px rgba(140, 127, 191, 0.5))',
                    transition: 'filter 0.3s'
                  }}
                >
                  <defs>
                    <linearGradient id="bodyGrad" x1="160" y1="50" x2="160" y2="250" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#1e1635" />
                      <stop offset="1" stopColor="#0e0b18" />
                    </linearGradient>
                    <linearGradient id="wingGrad" x1="0" y1="0" x2="320" y2="200" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#6d28d9" />
                      <stop offset="1" stopColor="#2e1065" />
                    </linearGradient>
                    <linearGradient id="plateGrad" x1="160" y1="120" x2="160" y2="220" gradientUnits="userSpaceOnUse">
                      <stop stopColor="var(--primary-color-hover, #a89fcf)" />
                      <stop offset="1" stopColor="#7c3aed" />
                    </linearGradient>
                    <linearGradient id="hornGrad" x1="160" y1="20" x2="160" y2="80" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#fbbf24" />
                      <stop offset="1" stopColor="#b45309" />
                    </linearGradient>

                  </defs>



                  {/* AILES GAUCHE ET DROITE */}
                  <g style={{ animation: 'wingBeat 3.5s ease-in-out infinite', transformOrigin: '160px 140px' }}>
                    {/* Aile Gauche */}
                    <path d="M 140 140 C 90 90 20 70 15 110 C 10 150 70 170 120 160 Z" fill="url(#wingGrad)" opacity="0.9" />
                    <path d="M 120 150 C 70 120 30 110 25 130 C 20 150 70 170 110 165 Z" fill="#3b0764" opacity="0.6" />
                    <path d="M 140 140 L 15 110 L 40 150 L 80 175 L 130 165" stroke="#a855f7" strokeWidth="2.5" strokeLinecap="round" />

                    {/* Aile Droite */}
                    <path d="M 180 140 C 230 90 300 70 305 110 C 310 150 250 170 200 160 Z" fill="url(#wingGrad)" opacity="0.9" />
                    <path d="M 200 150 C 250 120 290 110 295 130 C 300 150 250 170 210 165 Z" fill="#3b0764" opacity="0.6" />
                    <path d="M 180 140 L 305 110 L 280 150 L 240 175 L 190 165" stroke="#a855f7" strokeWidth="2.5" strokeLinecap="round" />
                  </g>

                  {/* QUEUE ÉPINEUSE */}
                  <path d="M 160 220 C 160 260 110 270 90 255 C 70 240 85 220 100 225" stroke="#7c3aed" strokeWidth="12" strokeLinecap="round" fill="none" />
                  <polygon points="80,245 70,265 95,260" fill="#fbbf24" />

                  {/* CORPS & TORSE PUISSANT */}
                  <path d="M 130 110 C 120 150 125 210 160 230 C 195 210 200 150 190 110 Z" fill="url(#bodyGrad)" stroke="#3b2d54" strokeWidth="3" />

                  {/* PLAQUES DE CUIRASSE PECTORALES */}
                  <path d="M 140 135 C 150 145 170 145 180 135 L 175 155 C 165 160 155 160 145 155 Z" fill="url(#plateGrad)" />
                  <path d="M 143 162 C 152 170 168 170 177 162 L 173 182 C 165 186 155 186 147 182 Z" fill="url(#plateGrad)" />
                  <path d="M 148 188 C 154 194 166 194 172 188 L 168 205 C 163 208 157 208 152 205 Z" fill="url(#plateGrad)" />

                  {/* COU & TÊTE DU DRAGON */}
                  <path d="M 140 110 L 135 70 L 160 50 L 185 70 L 180 110 Z" fill="url(#bodyGrad)" />

                  {/* CORNES DORÉES MASSIFS */}
                  <path d="M 140 60 C 125 40 100 30 85 35 C 95 50 115 58 135 68 Z" fill="url(#hornGrad)" stroke="#78350f" strokeWidth="1.5" />
                  <path d="M 180 60 C 195 40 220 30 235 35 C 225 50 205 58 185 68 Z" fill="url(#hornGrad)" stroke="#78350f" strokeWidth="1.5" />
                  
                  {/* CORNES SECONDAIRES */}
                  <path d="M 143 72 C 130 62 115 58 108 62 C 118 72 130 76 140 78 Z" fill="url(#hornGrad)" />
                  <path d="M 177 72 C 190 62 205 58 212 62 C 202 72 190 76 180 78 Z" fill="url(#hornGrad)" />

                  {/* MÂCHOIRE & MUSEAU */}
                  <polygon points="145,70 160,55 175,70 168,95 152,95" fill="#171126" stroke="#4a3b68" strokeWidth="2" />
                  
                  {/* NASEAUX AVEC ÉTINCELLES */}
                  <circle cx="156" cy="85" r="2" fill="var(--secondary-color, #6eb583)" />
                  <circle cx="164" cy="85" r="2" fill="var(--secondary-color, #6eb583)" />

                  {/* YEUX NÉON VIOLET ET VERT */}
                  <g style={{ animation: 'eyeGlow 2s infinite' }}>
                    <ellipse cx="152" cy="73" rx="4" ry="2.5" fill="var(--primary-color-hover, #a89fcf)" />
                    <ellipse cx="168" cy="73" rx="4" ry="2.5" fill="var(--primary-color-hover, #a89fcf)" />
                    <circle cx="152" cy="73" r="1.5" fill="var(--secondary-color, #6eb583)" />
                    <circle cx="168" cy="73" r="1.5" fill="var(--secondary-color, #6eb583)" />
                  </g>

                  {/* CROCS */}
                  <polygon points="154,92 156,98 158,92" fill="#fff" />
                  <polygon points="162,92 164,98 166,92" fill="#fff" />

                  {/* BARRE OLYMPIQUE */}
                  <rect x="70" y="195" width="180" height="10" rx="5" fill="#475569" stroke="#1e293b" strokeWidth="2" />
                  {/* DISQUES DE POIDS */}
                  <rect x="60" y="180" width="10" height="40" rx="3" fill="#1e1b4b" stroke="#6366f1" strokeWidth="2" />
                  <rect x="50" y="183" width="8" height="34" rx="2" fill="#312e81" stroke="#818cf8" strokeWidth="1.5" />
                  <rect x="250" y="180" width="10" height="40" rx="3" fill="#1e1b4b" stroke="#6366f1" strokeWidth="2" />
                  <rect x="262" y="183" width="8" height="34" rx="2" fill="#312e81" stroke="#818cf8" strokeWidth="1.5" />

                  {/* PATTES AVANT */}
                  <path d="M 125 185 C 120 195 130 205 135 195 Z" fill="#2d1b4e" stroke="var(--primary-color, #8c7fbf)" strokeWidth="1.5" />
                  <path d="M 185 185 C 190 195 200 195 195 185 Z" fill="#2d1b4e" stroke="var(--primary-color, #8c7fbf)" strokeWidth="1.5" />
                </svg>

                {/* DÉGÂTS FLOTTANTS */}
                {floatingDamages.map(item => (
                  <div
                    key={item.id}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      '--dx': `${item.x}px`,
                      '--dy': `${item.y}px`,
                      animation: 'damagePop 0.9s cubic-bezier(0.18, 0.89, 0.32, 1.28) forwards',
                      pointerEvents: 'none',
                      fontSize: item.isTap ? '0.9rem' : (item.isCrit ? '1.4rem' : '1.1rem'),
                      fontWeight: 900,
                      color: item.isTap ? '#fbbf24' : (item.isCrit ? '#ef4444' : 'var(--primary-color-hover, #a89fcf)'),
                      textShadow: '0 2px 8px rgba(0,0,0,0.9), 0 0 10px rgba(0,0,0,0.8)',
                      whiteSpace: 'nowrap',
                      zIndex: 10
                    }}
                  >
                    {item.text}
                  </div>
                ))}
              </div>

            </div>

            {/* BAS DU BLOC : TAUNT & INDICATION */}
            <div className="boss-arena-bottom" style={{ width: '100%', position: 'relative', zIndex: 1 }}>
              {/* TAUNT DU BOSS */}
              <div style={{
                fontSize: '0.82rem',
                color: isDead ? '#6b7280' : 'var(--text-secondary, #9a94ab)',
                fontWeight: 700,
                margin: '0.4rem 0 0.8rem 0'
              }}>
                « {isDead 
                  ? (boss.killer ? `Vaincu par ${boss.killer.name} et toute la commu !` : 'Argh... La commu a vaincu !')
                  : (boss.taunt || 'Pour info, balancer tout ton corps en arrière, ça ne compte pas comme une répétition.')} »
              </div>

              {/* INDICATION DE COMBAT NATURELLE (MASQUÉE QUAND LE BOSS EST VAINCU) */}
              {!isDead && (
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.06)',
                  padding: '0.5rem 0.85rem',
                  borderRadius: '12px',
                  fontSize: '0.74rem',
                  color: 'var(--text-secondary, #9a94ab)',
                  lineHeight: 1.4,
                  maxWidth: '92%'
                }}>
                  <span>💡 Tapote le dragon pour le harceler de micro-dégâts, ou termine tes vraies séances en salle pour lui infliger de lourds dégâts réels !</span>
                </div>
              )}
            </div>

          </div>

          {/* COLONNE DROITE : LES 3 CARTES DE RAID */}
          <div className="dashboard-cards-col">
            {renderContributionCard()}
            {renderTopHuntersCard()}
            {renderPrestigeTitlesCard()}
          </div>

        </div>
      )}

      </div>
    </div>
  );
}
